import React, { useState, useEffect } from 'react';
import { Table, Layout, Button, Card, Upload, message, Modal, Input, Typography, Space, Form, Alert, Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  UploadOutlined, ArrowLeftOutlined, AudioOutlined, FileTextOutlined, SettingOutlined, HistoryOutlined, DollarOutlined, LogoutOutlined, HomeOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api/auth';  // 添加这行

const { Header, Content, Footer } = Layout;

function RuleManager() {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rulesFile, setRulesFile] = useState(null);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [ruleContent, setRuleContent] = useState('');
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const navigate = useNavigate();

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/subtitles/rules', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setRules(data.rules);
      } else {
        message.error(t('rule.fetchFail'));
      }
    } catch (error) {
      console.error('fetch rule list error:', error);
      message.error(t('rule.fetchFail'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleRulesUpload = async () => {
    if (!rulesFile) {
      message.warning(t('rule.selectFileFirst'));
      return;
    }

    const formData = new FormData();
    formData.append('rules', rulesFile);

    try {
      const response = await fetch('/api/subtitles/upload-rules', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('rule.uploadSuccess'));
        setRulesFile(null);
        fetchRules();
      } else {
        message.error(`${t('rule.uploadFail')}: ${data.message}`);
      }
    } catch (error) {
      console.error('上传错误:', error);
      message.error(t('rule.uploadError'));
    }
  };

  const handleDeleteRule = async (id) => {
    if (!id) {
      message.error(t('rule.invalidId'));
      return;
    }
    
    Modal.confirm({
      title: t('common.confirmDelete'),
      content: t('rule.confirmDeleteMsg'),
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await fetch(`/api/subtitles/rules/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            message.success(t('common.deleteSuccess'));
            fetchRules();
          } else {
            message.error(data.message || t('common.deleteFail'));
          }
        } catch (error) {
          console.error('删除错误:', error);
          message.error(t('rule.deleteError'));
        }
      },
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('退出失败:', error);
      message.error(t('common.logoutFail'));
    }
  };

  const columns = [
    {
      title: t('rule.filename'),
      dataIndex: 'filename',
      key: 'filename',
    },
    {
      title: t('title.action'),
      key: 'action',
      render: (text, record) => (
        <Space>
          <Button onClick={() => handleEditRule(record)}>{t('common.edit')}</Button>
          <Button onClick={() => handleDeleteRule(record.id)}
            style={{ marginLeft: 8 }}
            danger
          >
            {t('button.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const handleCreateRule = async (values) => {
    try {
      const formData = new FormData();
      const blob = new Blob([values.content], { type: 'text/plain' });
      formData.append('rules', blob, `${values.name}.txt`);

      const response = await fetch('/api/subtitles/upload-rules', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('rule.createSuccess'));
        setRuleModalVisible(false);
        fetchRules();
      } else {
        message.error(data.message || t('rule.createFail'));
      }
    } catch (error) {
      console.error('创建规则错误:', error);
      message.error(t('rule.createFail'));
    }
  };

  const handleEditRule = (rule) => {
    setRuleContent(
      rule.rules
        .map(r => `${r.find},${r.replace}`)
        .join('\n')
    );
    setEditingRuleId(rule._id);  // 修改这里：从 rule.id 改为 rule._id
    setShowRuleEditor(true);
  };

  const ruleExample = `# ${t('rule.exampleHeader')}
find1,replace1
find2,replace2

# ${t('rule.exampleLine')}
# ${t('rule.exampleFormat')}
# ${t('rule.exampleEg')}
${t('rule.example1')}
${t('rule.example2')}`;

  return (
    <Layout className="layout">
      <Header style={{ background: '#001529', padding: '0 24px', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: '1', display: 'flex', alignItems: 'center' }}>
          <Space align="center">
            <Button 
              icon={<HomeOutlined />} 
              onClick={() => navigate('/dashboard')}
              style={{ 
                background: '#fff',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                height: '32px'
              }}
            >
              {t('title.back')}
            </Button>
            <Typography.Title 
              level={4} 
              style={{ 
                margin: 0,
                color: '#fff',
                lineHeight: '32px'
              }}
            >
              {t('menu.rules')}
            </Typography.Title>
          </Space>
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          items={[
            { key: 'audio', label: t('menu.audio'), icon: <AudioOutlined /> },
            { key: 'subtitle', label: t('menu.subtitle'), icon: <FileTextOutlined /> },
            { key: 'rules', label: t('menu.rules'), icon: <SettingOutlined /> },
            { key: 'history', label: t('menu.history'), icon: <HistoryOutlined /> },
            { key: 'plan', label: t('menu.plan'), icon: <DollarOutlined /> },
            { key: 'logout', label: t('menu.logout'), icon: <LogoutOutlined /> }
          ]}
          onClick={({ key }) => {
            switch(key) {
              case 'audio':
                navigate('/audio-manager');
                break;
              case 'subtitle':
                navigate('/subtitle-manager');
                break;
              case 'rules':
                navigate('/rule-manager');
                break;
              case 'history':
                navigate('/history');
                break;
              case 'plan':
        navigate('/plan');
        break;
              case 'logout':
                handleLogout();
                break;
            }
          }}
          style={{ background: '#001529' }}
        />
      </Header>
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px - 70px)' }}>
        <Card bordered={false}>
          <div>
            {/* 添加一个容器来放置上传和创建按钮 */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <div>
                <Upload
                  beforeUpload={(file) => {
                    const isTxtOrJson = file.type === 'text/plain' || file.type === 'application/json';
                    if (!isTxtOrJson) {
                      message.error(t('rule.uploadError'));
                      return false;
                    }
                    setRulesFile(file);
                    return false;
                  }}
                  accept=".txt,.json"
                  fileList={rulesFile ? [rulesFile] : []}
                >
                  <Button icon={<UploadOutlined />}>{t('rule.selectFile')}</Button>
                </Upload>
                <Button 
                  type="primary" 
                  onClick={handleRulesUpload} 
                  style={{ marginTop: '10px' }}
                >
                  {t('rule.uploadButton')}
                </Button>
              </div>
              <Button 
                type="primary" 
                onClick={() => setRuleModalVisible(true)}
              >
                {t('rule.create')}
              </Button>
            </div>

            {/* 表格部分 */}
            <Table
              columns={columns}
              dataSource={rules}
              rowKey="id"
              loading={loading}
            />    
                
            <Modal
              title={t('rule.create')}
              visible={ruleModalVisible}
              onCancel={() => setRuleModalVisible(false)}
              footer={null}
            >
              <div style={{ marginBottom: 16 }}>
                <p>{t('rule.editorInstructions')}</p>
                <p>{t('rule.editorExample')}</p>
                <pre style={{ background: '#f5f5f5', padding: 8 }}>
                  {ruleExample}
                </pre>
              </div>
              <Form onFinish={handleCreateRule}>
                <Form.Item
                  name="name"
                  label={t('rule.filename')}
                  rules={[{ required: true, message: t('rule.nameRequired') }]}
                >
                  <Input placeholder={t('rule.filename')} />
                </Form.Item>
                <Form.Item
                  name="content"
                  label={t('rule.contentLabel')}
                  rules={[{ required: true, message: t('rule.contentRequired') }]}
                >
                  <Input.TextArea 
                    rows={10} 
                    placeholder={t('rule.editorPlaceholder')}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    {t('rule.create')}
                  </Button>
                </Form.Item>
              </Form>
            </Modal>
            <Modal
              title={t('rule.editorTitle')}
              open={showRuleEditor}
              onOk={async () => {
                try {
                  const ruleLines = ruleContent.trim().split('\n');
                  const invalidLines = ruleLines.filter(line => {
                    const parts = line.trim().split(',');
                    return parts.length !== 2 || !parts[0].trim() || !parts[1].trim();
                  });

                  if (invalidLines.length > 0) {
                    message.error(t('rule.editorError'));
                    return;
                  }

                  const formData = new FormData();
                  const blob = new Blob([ruleContent], { type: 'text/plain' });
                  formData.append('rules', blob, 'rules.txt');

                  if (editingRuleId) {
                    formData.append('ruleId', editingRuleId);
                  }

                  const response = await fetch('/api/subtitles/upload-rules', {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                  });

                  const data = await response.json();
                  if (data.success) {
                    message.success(editingRuleId ? t('rule.editorUpdateSuccess') : t('rule.editorSaveSuccess'));
                    setShowRuleEditor(false);
                    setRuleContent('');
                    setEditingRuleId(null);
                    fetchRules();
                  } else {
                    message.error(data.message || t('rule.editorSaveFail'));
                  }
                } catch (error) {
                  console.error('保存规则错误:', error);
                  message.error(t('rule.editorSaveFail'));
                }
              }}
              onCancel={() => {
                setShowRuleEditor(false);
                setRuleContent('');
                setEditingRuleId(null);
              }}
            >
              <Input.TextArea
                value={ruleContent}
                onChange={e => setRuleContent(e.target.value)}
                placeholder={t('rule.editorPlaceholder')}
                rows={10}
              />
            </Modal>
          </div>
        </Card>
      </Content>
      <Footer style={{ textAlign: 'center' }}>
        {t('footer.copyright')} ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
}

export default RuleManager;